import React, { forwardRef, useImperativeHandle, useState } from 'react';
import * as Yup from 'yup';
import viacep from '~/services/viacep';
import getValidationErrors from '~/utils/getValidationErrors';

export interface FormAddressPropsRef {
  submit: () => Promise<FormData | null>;
  set: (value: FormData) => void;
}

interface FormAddressProps {
  test?: boolean;
}

interface FormData {
  zipcode: string;
  city: string;
  state: string;
  street: string;
  neighborhood: string;
  number: string;
  complement?: string;
}

const FormAddress: React.RefForwardingComponent<
  FormAddressPropsRef,
  FormAddressProps
> = ({ test }, ref) => {
  const [data, setData] = useState({} as FormData);
  const [errors, setErrors] = useState({} as any);

  useImperativeHandle(ref, () => ({
    set: (value: FormData) => {
      setData(value);
    },
    submit: async () => {
      try {
        setErrors({});

        const schema = Yup.object().shape({
          // zipcode: Yup.string().required('O cep é obrigatório'),
          // city: Yup.string().required('A cidade é obrigatória'),
          // state: Yup.string().required('* obrigatório'),
          street: Yup.string().required('O endereço é obrigatório'),
          neighborhood: Yup.string().required('O bairro é obrigatório'),
          number: Yup.string().required('* obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        let address = data.street;
        if (data.number) address += `, Nº ${data.number}`;
        if (data.neighborhood) address += `, ${data.neighborhood}`;
        if (data.complement) address += `, ${data.complement}`;
        if (data.city) address += `, ${data.city}`;
        if (data.state) address += ` - ${data.state}`;
        if (data.zipcode) address += `, ${data.zipcode}`;

        return { ...data, address };
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }

        return null;
      }
    },
  }));

  async function changeZipcode(value: string) {
    if (value.length === 8) {
      const response = await viacep.get(`${value}/json`);
      const { cep, logradouro, complemento, bairro, localidade, uf } =
        response.data;

      setData({
        ...data,
        street: logradouro,
        neighborhood: bairro,
        city: localidade,
        state: uf,
        complement: complemento,
        zipcode: cep,
      });
    }
  }

  return (
    <>
      <div className="col-lg-4">
        <div className="form-group mb-3">
          <label className="form-label">CEP</label>
          <div className="form-control-wrap">
            <input
              type="text"
              className="form-control"
              value={data.zipcode}
              onChange={event => {
                setData({ ...data, zipcode: event.target.value });
                changeZipcode(event.target.value);
              }}
            />
            {errors.zipcode && (
              <span className="invalid">{errors.zipcode}</span>
            )}
          </div>
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Cidade</label>
          <div className="form-control-wrap">
            <input
              type="text"
              className="form-control"
              value={data.city}
              onChange={event => {
                setData({ ...data, city: event.target.value });
              }}
            />
            {errors.city && <span className="invalid">{errors.city}</span>}
          </div>
        </div>
      </div>

      <div className="col-lg-2">
        <div className="form-group mb-3">
          <label className="form-label">UF</label>
          <div className="form-control-wrap">
            <input
              type="text"
              className="form-control"
              value={data.state}
              onChange={event => {
                setData({ ...data, state: event.target.value });
              }}
            />
            {errors.state && <span className="invalid">{errors.state}</span>}
          </div>
        </div>
      </div>

      <div className="col-lg-10">
        <div className="form-group mb-3">
          <label className="form-label">Endereço *</label>
          <div className="form-control-wrap">
            <input
              type="text"
              className="form-control"
              value={data.street}
              onChange={event => {
                setData({ ...data, street: event.target.value });
              }}
            />
            {errors.street && <span className="invalid">{errors.street}</span>}
          </div>
        </div>
      </div>

      <div className="col-lg-2">
        <div className="form-group mb-3">
          <label className="form-label">Número *</label>
          <div className="form-control-wrap">
            <input
              type="text"
              className="form-control"
              value={data.number}
              onChange={event => {
                setData({ ...data, number: event.target.value });
              }}
            />
            {errors.number && <span className="invalid">{errors.number}</span>}
          </div>
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Bairro *</label>
          <div className="form-control-wrap">
            <input
              type="text"
              className="form-control"
              value={data.neighborhood}
              onChange={event => {
                setData({
                  ...data,
                  neighborhood: event.target.value,
                });
              }}
            />
            {errors.neighborhood && (
              <span className="invalid">{errors.neighborhood}</span>
            )}
          </div>
        </div>
      </div>

      <div className="col-lg-6">
        <div className="form-group mb-3">
          <label className="form-label">Complemento</label>
          <div className="form-control-wrap">
            <input
              type="text"
              className="form-control"
              value={data.complement}
              onChange={event => {
                setData({
                  ...data,
                  complement: event.target.value,
                });
              }}
            />
            {errors.complement && (
              <span className="invalid">{errors.complement}</span>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default forwardRef(FormAddress);
